/**
 * FieldInfoTooltip — info icon that opens a standalone description modal.
 *
 * Separate touch target from the card click (which opens the edit modal).
 * Uses stopPropagation so tapping the icon does NOT trigger the card's onClick.
 */

import { useState } from "react";
import { Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface FieldInfoTooltipProps {
  /** Field description text */
  description: string;
  /** Optional field label for the modal title */
  label?: string;
}

export function FieldInfoTooltip({ description, label }: FieldInfoTooltipProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="flex items-center justify-center text-muted-foreground/25 hover:text-muted-foreground/50 transition-colors"
      >
        <Info className="h-4 w-4" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-[400px] rounded-2xl p-0 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          onPointerDownOutside={(e) => e.detail.originalEvent.stopPropagation()}
          onInteractOutside={(e) => e.detail.originalEvent.stopPropagation()}
        >
          <div className="px-8 pt-8 pb-6">
            <DialogHeader className="space-y-2">
              <div className="flex items-center justify-center h-12 w-12 rounded bg-[#007AFF]/10 mx-auto mb-2">
                <Info className="h-6 w-6 text-[#007AFF]" />
              </div>
              {label && (
                <DialogTitle className="text-[17px] font-semibold text-center">
                  {label}
                </DialogTitle>
              )}
              {!label && <DialogTitle className="sr-only">Field Info</DialogTitle>}
              <DialogDescription className="text-[15px] text-muted-foreground text-center leading-relaxed">
                {description}
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-8 pb-6">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full h-11 rounded-full bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] text-white text-[17px] font-normal transition-colors"
            >
              OK
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
